/**
 * App-wide form keyboard shortcuts: Cmd/Ctrl+Enter clicks the form's submit button, Esc clicks its cancel button.
 *
 * A "scope" is the nearest `<form>` or `[data-form-shortcuts]` element (for forms rendered without a `<form>`,
 * e.g. AddForm in modal mode). Buttons opt in explicitly with `data-form-submit` / `data-form-cancel`; otherwise
 * the form's default submit button and a button labelled "Cancel" are used. Clicking the real button keeps each
 * form's own disabled state, onClick logic and validation in charge.
 */

const SCOPE_SELECTOR = 'form, [data-form-shortcuts]'
// Radix dialogs/sheets/popovers and our custom Modal already close on Esc themselves.
const ESC_HANDLED_BY_SELECTOR = '[role="dialog"], [role="alertdialog"], [data-modal]'

const getScope = (el: Element | null) => el?.closest<HTMLElement>(SCOPE_SELECTOR) ?? null

const ownedBy = (scope: HTMLElement) => (btn: HTMLElement) => btn.closest(SCOPE_SELECTOR) === scope

const isUsable = (btn: HTMLElement | null | undefined): btn is HTMLElement =>
  !!btn && !(btn as HTMLButtonElement).disabled && btn.getAttribute('aria-disabled') !== 'true'

const findSubmitButton = (scope: HTMLElement) => {
  const owned = ownedBy(scope)
  const explicit = Array.from(scope.querySelectorAll<HTMLElement>('[data-form-submit]')).find(owned)
  if (explicit) return explicit
  if (!(scope instanceof HTMLFormElement)) return null
  // Same button the browser's implicit submission would use: the first submit button, including ones
  // outside the form that point at it via `form={id}`.
  return Array.from(scope.elements).find(
    (el): el is HTMLButtonElement | HTMLInputElement =>
      (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) && el.type === 'submit'
  )
}

const findCancelButton = (scope: HTMLElement) => {
  const owned = ownedBy(scope)
  const explicit = Array.from(scope.querySelectorAll<HTMLElement>('[data-form-cancel]')).find(owned)
  if (explicit) return explicit
  return Array.from(scope.querySelectorAll<HTMLButtonElement>('button')).find(
    (btn) => owned(btn) && btn.textContent?.trim().toLowerCase() === 'cancel'
  )
}

/** Clicks the submit button of the form containing `el`. Returns true if a button was clicked. */
export const triggerFormSubmit = (el: Element | null) => {
  const scope = getScope(el)
  const button = scope && findSubmitButton(scope)
  if (!isUsable(button)) return false
  button.click()
  return true
}

/** Clicks the cancel button of the form containing `el`. Returns true if a button was clicked. */
export const triggerFormCancel = (el: Element | null) => {
  const scope = getScope(el)
  if (!scope || scope.closest(ESC_HANDLED_BY_SELECTOR)) return false
  const button = findCancelButton(scope)
  if (!isUsable(button)) return false
  button.click()
  return true
}

export const handleFormShortcutKeyDown = (e: KeyboardEvent) => {
  // Skip keys something else already handled (Radix preventDefaults when a select/popover/dialog dismisses).
  if (e.defaultPrevented || e.isComposing || e.repeat) return
  const target = e.target instanceof Element ? e.target : null

  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
    if (triggerFormSubmit(target)) e.preventDefault()
    return
  }

  if (e.key === 'Escape' && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey)
    if (triggerFormCancel(target)) e.preventDefault()
}
